import { notFound } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Shared positive-integer schema behind every GT hub route PARAM validator
 * below — keeps `docId`/`ingestedFileId` validation consistent without a
 * bare `Number(...)` cast (which would silently produce `NaN` for a
 * malformed URL instead of throwing during route matching).
 */
const positiveIntParamSchema = z.coerce.number<number>().int().positive();

/** Parses a route param, throwing the router's `notFound()` (not a raw ZodError) when malformed. */
function parseIdParam(raw: string): number {
    const result = positiveIntParamSchema.safeParse(raw);
    if (!result.success) throw notFound();
    return result.data;
}

/** PARAM validator for the standalone `$docId` GT editor routes (nested doc `rows`). */
export const docIdParams = {
    parse: (raw: { docId: string }) => ({
        docId: parseIdParam(raw.docId),
    }),
    stringify: (params: { docId: number }) => ({
        docId: String(params.docId),
    }),
};

/** PARAM validator for the `$ingestedFileId` GT hub file-level routes. */
export const ingestedFileIdParams = {
    parse: (raw: { ingestedFileId: string }) => ({
        ingestedFileId: parseIdParam(raw.ingestedFileId),
    }),
    stringify: (params: { ingestedFileId: number }) => ({
        ingestedFileId: String(params.ingestedFileId),
    }),
};

/** PARAM validator for the nested `$ingestedFileId/$docId` GT hub doc-level routes. */
export const ingestedFileIdAndDocIdParams = {
    parse: (raw: { ingestedFileId: string; docId: string }) => ({
        ingestedFileId: parseIdParam(raw.ingestedFileId),
        docId: parseIdParam(raw.docId),
    }),
    stringify: (params: { ingestedFileId: number; docId: number }) => ({
        ingestedFileId: String(params.ingestedFileId),
        docId: String(params.docId),
    }),
};
